import { useCallback, useEffect, useId, useLayoutEffect, useRef } from 'react';
import { toast, type Id } from 'react-toastify';
import { ToastMessage } from '@shared/components/ui/toast/ToastMessage';
import { toastOptionsFor } from '@shared/components/ui/toast/toastOptions';
import type { QuoteStep } from '../types/quote.types';
import { PAYMENT_RECONFIRM_ACTION_LABEL, PAYMENT_RECONFIRM_REASON } from '../utils/paymentAmountReconfirm';

const RECONFIRM_TOAST_ID = 'quote-payment-reconfirm';

interface UseReconfirmToastOptions {
    required: boolean;
    currentStep: QuoteStep;
    goToStep: (step: QuoteStep) => void;
}

export const useReconfirmToast = ({ required, currentStep, goToStep }: UseReconfirmToastOptions) => {
    const instanceId = useId();
    const goToStepRef = useRef(goToStep);
    useLayoutEffect(() => {
        goToStepRef.current = goToStep;
    }, [goToStep]);
    const shownToastIdRef = useRef<Id | null>(null);
    const shownCountRef = useRef(0);
    const visible = required && currentStep === 'summary';

    const show = useCallback(() => {
        const action = {
            label: PAYMENT_RECONFIRM_ACTION_LABEL,
            onClick: () => goToStepRef.current('payment'),
        };
        if (shownToastIdRef.current !== null && toast.isActive(shownToastIdRef.current)) return;
        shownCountRef.current += 1;
        shownToastIdRef.current = toast.warning(<ToastMessage message={PAYMENT_RECONFIRM_REASON} action={action} />, {
            toastId: `${RECONFIRM_TOAST_ID}-${instanceId}-${shownCountRef.current}`,
            ...toastOptionsFor(action),
        });
    }, [instanceId]);

    useEffect(() => {
        if (!visible) return undefined;
        show();
        return () => {
            if (shownToastIdRef.current !== null) toast.dismiss(shownToastIdRef.current);
            shownToastIdRef.current = null;
        };
    }, [visible, show]);

    const reshow = useCallback(() => {
        if (visible) show();
    }, [visible, show]);

    return { reshow };
};
